import React, { useEffect, useRef, useState } from "react";
import { SERVER_ENDPOINT } from "../../configs/env";
import "../../assets/css/subscription_form.scss";
import logoSisa from "../../assets/images/pocket_peace/sisa_logo.png";
import { ezEduPaymentByOrderId, ezEduPaymentRequest } from "../../api/payment";
import { mapToQueryString, parseJavaMapString } from "../../utils/common";

const SisaSubscriptionForm = ({
  phoneNumber,
  userRef = "",
  amount = 10000,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    postcode: "",
    basicAddress: "",
    detailAddress: "",
    grade: "",
    phone: "",
    desiredMonth: "",
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const layerRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      alert("이름을 입력해주세요.");
      return;
    }
    if (!formData.postcode || !formData.basicAddress) {
      alert("배송지 주소를 입력해주세요.");
      return;
    }
    if (!formData.grade) {
      alert("학년을 입력해주세요.");
      return;
    }
    if (!formData.phone) {
      alert("휴대폰 번호를 입력해주세요.");
      return;
    }
    if (!formData.desiredMonth) {
      alert("구독 시작 희망 월호를 입력해주세요.");
      return;
    }

    try {
      setIsLoading(true);

      const response = await ezEduPaymentRequest(
        1,
        "SISA",
        formData.name,
        formData.postcode,
        formData.basicAddress,
        formData.detailAddress || "",
        formData.grade,
        formData.phone,
        formData.desiredMonth,
        amount,
        "card",
        "시사원정대 구독",
        "N",
        userRef
      );

      if (response.success && response.data) {
        const paymentWindow = openPaymentPopup(response.data);
        if (paymentWindow) {
          monitorPaymentPopup(paymentWindow, response.data.orderNumber);
        }
      } else {
        alert(response.error?.message || "결제 요청 중 오류가 발생했습니다.");
        if (onPaymentError) {
          onPaymentError(response.error);
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error("결제 요청 실패:", error);
      alert("결제 요청 중 오류가 발생했습니다.");
      if (onPaymentError) {
        onPaymentError(error);
      }
      setIsLoading(false);
    }
  };

  const monitorPaymentPopup = (paymentWindow, orderNumber) => {
    const checkInterval = setInterval(() => {
      if (paymentWindow.closed) {
        clearInterval(checkInterval);
        setIsLoading(false);

        checkPaymentResult(orderNumber);
      }
    }, 500);
  };

  const checkPaymentResult = async (orderNumber) => {
    const response = await ezEduPaymentByOrderId(orderNumber);

    if (response.success && response.data.status === "completed") {
      handlePaymentComplete({
        orderNumber: orderNumber,
        ...response.data,
      });
    }
  };

  const openPaymentPopup = (paymentData) => {
    const { orderNumber, paymentFormUrl, formParams } = paymentData;

    let paramsMap;
    if (typeof formParams === "string") {
      paramsMap = parseJavaMapString(formParams);
    } else if (typeof formParams === "object" && formParams !== null) {
      paramsMap = formParams;
    } else {
      alert("결제 정보를 처리할 수 없습니다.");
      return null;
    }

    const formParamsQueryString = mapToQueryString(paramsMap);

    const params = new URLSearchParams({
      orderNumber: orderNumber,
      paymentFormUrl: paymentFormUrl || "KSNET_POPUP",
      formParams: formParamsQueryString,
      autoStart: "true",
    });

    const paymentUrl = `${
      SERVER_ENDPOINT
    }/view/ez_edu_payment?${params.toString()}`;

    const popupOptions = [
      "width=800",
      "height=700",
      "scrollbars=yes",
      "resizable=yes",
      "toolbar=no",
      "menubar=no",
      "location=no",
      "directories=no",
      "status=no",
    ].join(",");

    const paymentWindow = window.open(
      paymentUrl,
      "ezEduPaymentWindow",
      popupOptions
    );

    if (!paymentWindow) {
      alert("팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.");
      return null;
    }

    return paymentWindow;
  };

  const handlePaymentComplete = (paymentResult) => {
    onPaymentSuccess && onPaymentSuccess(paymentResult, formData);
  };

  const handlePaymentFailed = (errorData) => {
    console.log("결제 실패", errorData);

    alert(`결제에 실패했습니다.\n${errorData.message || "다시 시도해주세요."}`);

    if (onPaymentError) {
      onPaymentError(errorData);
    }
  };

  const openDaumPostcode = () => {
    setIsOpen(true);
    const elementLayer = layerRef.current;
    const embedLayer = elementLayer.querySelector(".daum-postcode-embed");

    const width = 400;
    const height = 480;
    const borderWidth = 2;

    elementLayer.style.display = "block";
    elementLayer.style.width = `${width}px`;
    elementLayer.style.height = `${height}px`;

    new window.daum.Postcode({
      oncomplete: function (data) {
        console.log("선택된 주소:", data);

        let fullAddress = data.address;
        let extraAddress = "";

        if (data.addressType === "R") {
          if (data.bname !== "") {
            extraAddress += data.bname;
          }
          if (data.buildingName !== "") {
            extraAddress +=
              extraAddress !== ""
                ? `, ${data.buildingName}`
                : data.buildingName;
          }
          fullAddress += extraAddress !== "" ? ` (${extraAddress})` : "";
        }

        setFormData((prev) => ({
          ...prev,
          postcode: data.zonecode,
          basicAddress: fullAddress,
        }));

        closeDaumPostcode();
      },
      width: "100%",
      height: "100%",
    }).embed(embedLayer);

    const left = (window.innerWidth - width) / 2 - borderWidth;
    const top = (window.innerHeight - height) / 2 - borderWidth;
    elementLayer.style.left = `${left}px`;
    elementLayer.style.top = `${top}px`;
  };

  const closeDaumPostcode = () => {
    setIsOpen(false);
    const elementLayer = layerRef.current;
    elementLayer.style.display = "none";
  };

  useEffect(() => {
    const scriptId = "daum-postcode-script";
    if (document.getElementById(scriptId)) return;

    const script = document.createElement("script");
    script.id = scriptId;
    script.src =
      "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      phone: phoneNumber,
    }));
  }, [phoneNumber]);

  return (
    <div className="subscription-page">
      <div className="subscription-container">
        <div className="subscription-logo sisa-logo">
          <img src={logoSisa} alt="시사원정대" />
        </div>

        <div className="subscription-form">
          <h2 className="form-title">독자 정보 입력하기</h2>
          <p className="form-description">
            시사원정대를 받아보실 수 있도록
            <br />
            정보와 주소를 등록해 주세요.
          </p>

          <div className="form-group">
            <label className="form-label">이름</label>
            <input
              type="text"
              name="name"
              className="form-input"
              placeholder="이름"
              value={formData.name}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">배송지 주소</label>

            <div className="address-input-group">
              <input
                type="text"
                name="postcode"
                className="form-input"
                placeholder="우편번호"
                value={formData.postcode}
                onChange={handleChange}
                readOnly
              />
              <button
                type="button"
                className="address-search-button"
                style={{ height: "42px" }}
                onClick={openDaumPostcode}
                disabled={isLoading}
              >
                주소검색
              </button>
            </div>

            <input
              type="text"
              name="basicAddress"
              className="form-input"
              placeholder="기본주소"
              value={formData.basicAddress}
              onChange={handleChange}
              readOnly
            />

            <input
              type="text"
              name="detailAddress"
              className="form-input"
              placeholder="상세주소"
              value={formData.detailAddress}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">학년</label>
            <input
              type="text"
              name="grade"
              className="form-input"
              placeholder="학년"
              value={formData.grade}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">휴대폰 번호</label>
            <input
              type="tel"
              name="phone"
              className="form-input"
              placeholder="휴대폰 번호"
              value={formData.phone}
              onChange={handleChange}
              disabled={true}
            />
          </div>

          <div className="form-group">
            <label className="form-label">구독 시작 희망 월호</label>
            <input
              type="text"
              name="desiredMonth"
              className="form-input"
              placeholder="OO년 OO월호"
              value={formData.desiredMonth}
              onChange={handleChange}
              disabled={isLoading}
            />
            <p className="form-help-text">
              기존 구독 연장이신분들은 <u>1833-3503</u>으로 문의 바랍니다.
            </p>
          </div>

          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "처리 중..." : "구독하기"}
          </button>

          <p className="payment-notice">
            * 결제 버튼 클릭 시 결제 페이지로 이동합니다.
          </p>
        </div>

        <div
          ref={layerRef}
          className="daum-postcode-layer"
          style={{
            display: "none",
            position: "fixed",
            zIndex: 1000,
            backgroundColor: "white",
            border: "2px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          }}
        >
          <div className="postcode-header">
            <button
              type="button"
              onClick={closeDaumPostcode}
              className="postcode-close-button"
            >
              ✕
            </button>
          </div>
          <div className="daum-postcode-embed" style={{ height: "100%" }}></div>
        </div>
      </div>
    </div>
  );
};

export default SisaSubscriptionForm;
